import { memo, type SVGProps } from 'react';
import { Square, squareToCell } from '@fog-chess/core';
import { cn } from '@/lib/utils';

export interface MoveArrowProps extends SVGProps<SVGSVGElement> {
  /** Source square */
  from: Square;
  /** Target square */
  to: Square;
  /** Mover's color (for arrow color) */
  color: 'white' | 'black';
  /** Square size in pixels */
  squareSize?: number;
  /** Board orientation (white = standard, black = flipped) */
  flipped?: boolean;
}

/**
 * MoveArrow — SVG arrow overlay for last move indication.
 *
 * Draws a curved arrow from center of `from` square to center of `to` square.
 * Positioned absolutely over the board grid. Purely visual, memoized.
 */
export const MoveArrow = memo(function MoveArrow({
  from,
  to,
  color,
  squareSize = 56,
  flipped = false,
  className,
  style,
  ...rest
}: MoveArrowProps) {
  const fromCell = squareToCell(from);
  const toCell = squareToCell(to);

  // Convert grid coordinates to pixel coordinates (center of squares)
  // If flipped (black perspective), invert both axes
  const fx = flipped ? 7 - fromCell.col : fromCell.col;
  const fy = flipped ? 7 - fromCell.row : fromCell.row;
  const tx = flipped ? 7 - toCell.col : toCell.col;
  const ty = flipped ? 7 - toCell.row : toCell.row;

  const x1 = (fx + 0.5) * squareSize;
  const y1 = (fy + 0.5) * squareSize;
  const x2 = (tx + 0.5) * squareSize;
  const y2 = (ty + 0.5) * squareSize;

  // Calculate control point for slight curve (bezier)
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const curve = Math.min(dist * 0.15, 12); // Subtle curve, max 12px
  const angle = Math.atan2(dy, dx);
  const cx = x1 + dx / 2 - Math.sin(angle) * curve;
  const cy = y1 + dy / 2 + Math.cos(angle) * curve;

  // Arrowhead geometry
  const headLength = 10;
  const headAngle = Math.PI / 6; // 30 degrees
  const arrowAngle = Math.atan2(y2 - cy, x2 - cx);

  const xHead1 = x2 - headLength * Math.cos(arrowAngle - headAngle);
  const yHead1 = y2 - headLength * Math.sin(arrowAngle - headAngle);
  const xHead2 = x2 - headLength * Math.cos(arrowAngle + headAngle);
  const yHead2 = y2 - headLength * Math.sin(arrowAngle + headAngle);

  // Color based on mover
  const strokeColor = color === 'white' ? 'hsl(var(--primary))' : 'hsl(var(--accent))';

  // Suppress unused variable warnings
  void xHead1; void yHead1; void xHead2; void yHead2;

  return (
    <svg
      className={cn('pointer-events-none absolute inset-0', className)}
      style={{
        ...style,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'visible',
      }}
      {...rest}
    >
      <defs>
        <marker
          id={`arrowhead-${from}-${to}`}
          markerWidth={10}
          markerHeight={7}
          refX={9}
          refY={3.5}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            d="M0,0 L0,7 L10,3.5 Z"
            fill={strokeColor}
            opacity={0.8}
          />
        </marker>
      </defs>

      {/* Curved path with arrowhead */}
      <path
        d={`M${x1} ${y1} Q${cx} ${cy} ${x2} ${y2}`}
        stroke={strokeColor}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={0.8}
        markerEnd={`url(#arrowhead-${from}-${to})`}
        filter="drop-shadow(0 1px 2px hsl(var(--primary) / 0.3))"
      />
    </svg>
  );
}, (prev, next) => {
  return (
    prev.from === next.from &&
    prev.to === next.to &&
    prev.color === next.color &&
    prev.squareSize === next.squareSize &&
    prev.flipped === next.flipped
  );
});

MoveArrow.displayName = 'MoveArrow';